import { useCallback } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useApolloCompatibleMutation } from '@confluence/apollo-relay-interop/entry-points/useApolloCompatibleMutation';
import type { BadStatusError } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import type { FetchOptions } from './fetchResponse';
import { useFetchData } from './useFetchData';
import type { TransformErrorType } from './handleError';
import { convertToApolloError } from './convertToApolloError';
import { useExperimentalMutationLegacy } from './useExperimentalMutationLegacy';

type MutationResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	called: boolean;
};

type UseExperimentalMutationParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void | Promise<void>;
	onError?: (error: ApolloErrorInterop) => Promise<void> | void;
	transformError?: TransformErrorType;
	maxRetries?: number;
	variables?: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

type MutationFunctionOptions<TData, TVariables> = {
	variables?: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

export function useExperimentalMutation<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	credentials,
	parseResult,
	parseResponseOnSuccess,
	parseResponseOnError,
	onCompleted,
	onError,
	transformError,
	maxRetries = 0,
	variables: defaultVariables,
	update,
	optimisticResponse,
}: UseExperimentalMutationParams<TData, TVariables, TResponse>): [
	(options?: MutationFunctionOptions<TData, TVariables>) => Promise<{ data: TData | undefined }>,
	MutationResult<TData>,
] {
	if (!expValEquals('cc_move_legacy-schema-bridge-code-into-interops', 'isEnabled', true)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
		return useExperimentalMutationLegacy({
			url,
			method,
			headers,
			body,
			credentials,
			parseResult,
			parseResponseOnSuccess,
			parseResponseOnError,
			onCompleted,
			onError,
			transformError,
			maxRetries,
			variables: defaultVariables,
			update,
			optimisticResponse,
		});
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		parseResult,
		parseResponseOnSuccess,
		parseResponseOnError,
		variables: defaultVariables,
	});

	// eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
	const fetchDataWithRetry = useCallback(
		async (variables?: TVariables, count = 0): Promise<TData> => {
			try {
				return await fetchData(variables);
			} catch (error) {
				if (count < maxRetries && error?.response?.status === 409) {
					return fetchDataWithRetry(variables, ++count);
				}
				throw error;
			}
		},
		[maxRetries, fetchData],
	);

	// eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
	return useExperimentalMutationBase<TData, TVariables>({
		fetchData: fetchDataWithRetry,
		onCompleted,
		onError,
		transformError,
		update,
		optimisticResponse,
	});
}

type UseExperimentalMutationBaseParams<TData, TVariables> = {
	fetchData: (variables?: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void | Promise<void>;
	onError?: (error: ApolloErrorInterop) => void;
	transformError?: TransformErrorType;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

export function useExperimentalMutationBase<TData, TVariables>({
	fetchData,
	onCompleted,
	onError,
	transformError,
	update,
	optimisticResponse,
}: UseExperimentalMutationBaseParams<TData, TVariables>): [
	(options?: MutationFunctionOptions<TData, TVariables>) => Promise<{ data: TData | undefined }>,
	MutationResult<TData>,
] {
	const handleConvertToApolloError = async (error: Error | BadStatusError) => {
		return convertToApolloError((await transformError?.(error)) || error);
	};

	return useApolloCompatibleMutation<TData, TVariables>({
		fetchData,
		onCompleted,
		onError,
		update,
		optimisticResponse,
		convertToApolloError: handleConvertToApolloError,
	});
}
